<template>
  <div class="auth-page auth-page--register">
    <header class="auth-card__header auth-card__header--register">
      <h1 class="auth-card__title auth-card__title--register">Création de compte</h1>
      <p class="auth-card__subtitle auth-card__subtitle--register">
        <span class="auth-card__subtitle-mark">*</span>
        Pour bénéficier de ce service rassurez vous de remplir les conditions. Notamment avoir un revenu mensuel minimum net de 750&nbsp;000&nbsp;FCFA.
      </p>
    </header>
    <div class="auth-page__wrap auth-page__wrap--wide">
      <Transition name="card" appear>
        <div class="auth-card auth-card--wide">
          <form class="auth-form auth-form--grid" @submit.prevent="onFormSubmit">
            <div class="auth-form__group auth-form__group--full">
              <div class="progress-brand__labels">
                <span :class="{ 'is-active': registerStep === 1 }">Identité</span>
                <span :class="{ 'is-active': registerStep === 2 }">Situation</span>
                <span :class="{ 'is-active': registerStep === 3 }">Sécurité</span>
              </div>
              <div class="progress-brand" role="progressbar" :aria-valuenow="registerStep" aria-valuemin="1" aria-valuemax="3" aria-label="Progression de l’inscription">
                <div class="progress-brand__step" :class="{ 'is-active': registerStep >= 1, 'is-done': registerStep > 1 }" />
                <div class="progress-brand__step" :class="{ 'is-active': registerStep >= 2, 'is-done': registerStep > 2 }" />
                <div class="progress-brand__step" :class="{ 'is-active': registerStep >= 3 }" />
              </div>
            </div>

            <template v-if="registerStep === 1">
            <div class="auth-form__group">
              <label for="last_name" class="auth-form__label">Nom <span class="text-red-500">*</span></label>
              <div class="auth-form__input-wrap">
                <UserIcon class="auth-form__icon" />
                <input
                  id="last_name"
                  v-model="last_name"
                  type="text"
                  class="auth-form__input"
                  required
                  placeholder="Votre nom"
                  @input="updateGeneratedLogin"
                />
              </div>
            </div>
            <div class="auth-form__group">
              <label for="first_name" class="auth-form__label">Prénom(s) <span class="text-red-500">*</span></label>
              <div class="auth-form__input-wrap">
                <UserIcon class="auth-form__icon" />
                <input
                  id="first_name"
                  v-model="first_name"
                  type="text"
                  class="auth-form__input"
                  required
                  placeholder="Votre prénom"
                  @input="updateGeneratedLogin"
                />
              </div>
            </div>
            <div class="auth-form__group auth-form__group--full">
              <label for="username" class="auth-form__label">Nom d'utilisateur</label>
              <div class="auth-form__input-wrap">
                <IdentificationIcon class="auth-form__icon" />
                <input
                  id="username"
                  :value="username"
                  type="text"
                  class="auth-form__input auth-form__input--readonly"
                  readonly
                  placeholder="Généré automatiquement"
                />
              </div>
            </div>
            <div class="auth-form__group auth-form__group--full">
              <label for="email" class="auth-form__label">Email <span class="text-red-500">*</span></label>
              <div class="auth-form__input-wrap">
                <EnvelopeIcon class="auth-form__icon" />
                <input
                  id="email"
                  v-model="email"
                  type="email"
                  class="auth-form__input"
                  required
                  placeholder="vous@exemple.com"
                />
              </div>
            </div>
            <div class="auth-form__submit-wrap auth-form__group--full auth-form__nav">
              <button type="button" class="btn-brand btn-brand--primary auth-form__continue" :disabled="!step1Valid" @click="goRegisterStep(2)">
                Continuer
              </button>
            </div>
            </template>

            <template v-else-if="registerStep === 2">
            <div class="auth-form__group auth-form__group--full">
              <label for="tel" class="auth-form__label">Numéro de téléphone <span class="text-red-500">*</span></label>
              <div class="auth-form__input-wrap">
                <PhoneIcon class="auth-form__icon" />
                <input
                  id="tel"
                  v-model="tel"
                  type="tel"
                  class="auth-form__input"
                  required
                  autocomplete="tel"
                  placeholder="Ex: +225 07 00 00 00 00"
                />
              </div>
            </div>
            <div class="auth-form__group auth-form__group--full">
              <label for="adresse" class="auth-form__label">Adresse <span class="text-red-500">*</span></label>
              <div class="auth-form__input-wrap">
                <MapPinIcon class="auth-form__icon" />
                <input
                  id="adresse"
                  v-model="adresse"
                  type="text"
                  class="auth-form__input"
                  required
                  placeholder="Votre adresse"
                />
              </div>
            </div>
            <div class="auth-form__group auth-form__group--full">
              <label for="fonction" class="auth-form__label">Fonction <span class="text-red-500">*</span></label>
              <div class="auth-form__input-wrap">
                <BriefcaseIcon class="auth-form__icon" />
                <input
                  id="fonction"
                  v-model="fonction"
                  type="text"
                  class="auth-form__input"
                  required
                  placeholder="Votre fonction"
                />
              </div>
            </div>
            <div class="auth-form__submit-wrap auth-form__group--full auth-form__nav">
              <button type="button" class="btn-brand btn-brand--secondary" @click="goRegisterStep(1)">Retour</button>
              <button type="button" class="btn-brand btn-brand--primary auth-form__continue" :disabled="!step2Valid" @click="goRegisterStep(3)">
                Continuer
              </button>
            </div>
            </template>

            <template v-else>
            <div class="auth-form__group auth-form__group--full">
              <label for="password" class="auth-form__label">Mot de passe <span class="text-red-500">*</span></label>
              <div class="auth-form__input-wrap">
                <LockClosedIcon class="auth-form__icon" />
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="auth-form__input auth-form__input--with-btn"
                  required
                  placeholder="••••••••"
                  @input="validatePassword"
                />
                <button type="button" class="auth-form__toggle-pwd" aria-label="Afficher / masquer" @click="showPassword = !showPassword">
                  <EyeIcon v-if="!showPassword" class="w-5 h-5" />
                  <EyeSlashIcon v-else class="w-5 h-5" />
                </button>
              </div>
              <div v-if="password" class="auth-form__strength">
                <div
                  v-for="(label, key) in strengthLabels"
                  :key="key"
                  class="auth-form__strength-item"
                  :class="{ 'auth-form__strength-item--ok': passwordChecks[key] }"
                >
                  <CheckIcon v-if="passwordChecks[key]" class="w-4 h-4" />
                  <span v-else class="auth-form__strength-dot" />
                  {{ label }}
                </div>
              </div>
            </div>
            <div class="auth-form__group auth-form__group--full">
              <label for="passwordConfirm" class="auth-form__label">Confirmer le mot de passe <span class="text-red-500">*</span></label>
              <div class="auth-form__input-wrap">
                <LockClosedIcon class="auth-form__icon" />
                <input
                  id="passwordConfirm"
                  v-model="passwordConfirm"
                  :type="showConfirm ? 'text' : 'password'"
                  class="auth-form__input auth-form__input--with-btn"
                  required
                  placeholder="••••••••"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="auth-form__toggle-pwd"
                  :aria-label="showConfirm ? 'Masquer la confirmation' : 'Afficher la confirmation'"
                  @click="showConfirm = !showConfirm"
                >
                  <EyeIcon v-if="!showConfirm" class="w-5 h-5" />
                  <EyeSlashIcon v-else class="w-5 h-5" />
                </button>
              </div>
              <p v-if="passwordConfirm && password !== passwordConfirm" class="auth-form__field-error">
                Les mots de passe ne correspondent pas.
              </p>
            </div>
            <div v-if="errorMessage" class="auth-form__error auth-form__group--full">
              <ExclamationCircleIcon class="w-5 h-5 shrink-0" />
              <span>{{ errorMessage }}</span>
            </div>

            <div
              v-if="captchaProvider === 'turnstile'"
              class="auth-form__group auth-form__group--full"
            >
              <div ref="turnstileContainerRef" class="turnstile-widget" />
            </div>

            <div class="auth-form__submit-wrap auth-form__group--full auth-form__nav">
              <button type="button" class="btn-brand btn-brand--secondary" @click="goRegisterStep(2)">Retour</button>
              <button type="submit" class="auth-form__submit" :disabled="loading || !isFormValid || (captchaProvider === 'turnstile' && !turnstileToken)">
                <span v-if="!loading" class="auth-form__submit-text">Créer un compte</span>
                <span v-else class="auth-form__spinner" aria-hidden="true" />
              </button>
            </div>
            </template>

            <p class="auth-form__recaptcha auth-form__group--full">
              <!-- Ce formulaire est protégé par Cloudflare. -->
              <a href="/politique-de-confidentialite" target="_blank" rel="noopener">Politique de confidentialité</a>
              et
              <a href="/conditions-generales-utilisation" target="_blank" rel="noopener">Conditions d'utilisation</a>.
            </p>
            <p class="auth-form__footer auth-form__group--full">
              Déjà un compte ?
              <router-link to="/connexion" class="auth-form__link">Se connecter</router-link>
            </p>
          </form>
        </div>
      </Transition>
    </div>

    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showEmailVerificationModal" class="auth-modal-overlay" @click.self="closeEmailVerificationModal">
          <div class="auth-modal-card">
            <div class="auth-modal-icon">
              <EnvelopeIcon class="w-16 h-16 text-amber-500" />
            </div>
            <p class="auth-modal-brand">patrimonea by Gwem</p>
            <h2 class="auth-modal-title">Vérifiez votre adresse email</h2>
            <p class="auth-modal-text">
              Un code de vérification a été envoyé à l'adresse suivante :
              <strong>{{ email }}</strong>. Saisissez-le ci-dessous pour activer votre compte.
            </p>

            <form class="auth-form" @submit.prevent="submitEmailVerification">
              <div class="auth-form__group auth-form__group--full">
                <label for="verificationCode" class="auth-form__label">Code de vérification</label>
                <div class="auth-form__input-wrap">
                  <input
                    id="verificationCode"
                    v-model="verificationCode"
                    type="text"
                    class="auth-form__input"
                    required
                    maxlength="6"
                    placeholder="Code à 6 chiffres"
                  />
                </div>
              </div>

              <div v-if="verificationError" class="auth-form__error auth-form__group--full">
                <ExclamationCircleIcon class="w-5 h-5 shrink-0" />
                <span>{{ verificationError }}</span>
              </div>

              <div class="auth-form__submit-wrap auth-form__group--full">
                <button type="submit" class="auth-form__submit" :disabled="verificationLoading">
                  <span v-if="!verificationLoading" class="auth-form__submit-text">Valider le code</span>
                  <span v-else class="auth-form__spinner" aria-hidden="true" />
                </button>
              </div>

              <p class="auth-form__footer auth-form__group--full">
                Vous n'avez pas reçu de code ?
                <button
                  type="button"
                  class="auth-form__link"
                  :disabled="resendLoading"
                  @click="resendVerificationCode"
                >
                  Renvoyer le code
                </button>
              </p>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showSuccessModal" class="auth-modal-overlay" @click.self="closeSuccessModal">
          <div class="auth-modal-card">
            <div class="auth-modal-icon">
              <CheckCircleIcon class="w-16 h-16 text-green-600" />
            </div>
            <p class="auth-modal-brand">patrimonea by Gwem</p>
            <h2 class="auth-modal-title">Compte créé avec succès</h2>
            <p class="auth-modal-text">
              Votre adresse email a bien été vérifiée. Vous pouvez maintenant vous connecter à votre espace.
            </p>

            <div class="auth-form__submit-wrap auth-form__group--full">
              <button type="button" class="auth-form__submit" @click="continueToLogin">
                <span class="auth-form__submit-text">Aller à la connexion</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  resolveTurnstileSiteKey,
  mountTurnstileWidget,
  resetTurnstileWidget as resetTurnstileWidgetUtil,
} from '@/utils/turnstile';
import {
  UserIcon,
  IdentificationIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  BriefcaseIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  CheckIcon,
} from '@heroicons/vue/24/outline';
import axios from 'axios';
import { getErrorMessage, formatValidationErrors } from '@/utils/errorMessages';

import { resolveApiPrefix } from '@/utils/apiOrigin';
import { createSubmitGuard, formatThrottleMessage } from '@/utils/submitGuard';
import { loadRegisterPrefillFromDiagnostic } from '@/utils/diagnosticPrefill';
import { trackSignUp } from '@/utils/analytics';

const API_PREFIX = resolveApiPrefix();
const registerSubmitGuard = createSubmitGuard(4000);

const router = useRouter();
const route = useRoute();
const registerStep = ref(1);
const last_name = ref('');
const first_name = ref('');
const username = ref('');
const nextClientRank = ref(null); // null = pas encore chargé, évite d'afficher "...210001" avant le vrai rang
const email = ref('');
const tel = ref('');
const adresse = ref('');
const fonction = ref('');
const password = ref('');
const passwordConfirm = ref('');
const showPassword = ref(false);
const showConfirm = ref(false);
const loading = ref(false);
const errorMessage = ref('');
const turnstileToken = ref('');
const turnstileContainerRef = ref(null);
const turnstileWidgetId = ref(null);
const turnstileSiteKey = ref(resolveTurnstileSiteKey());
const captchaProvider = computed(() => (turnstileSiteKey.value ? 'turnstile' : 'none'));
const showSuccessModal = ref(false);
const showEmailVerificationModal = ref(false);
const passwordChecks = ref({
  length: false,
  uppercase: false,
  lowercase: false,
  digit: false,
  special: false,
});

const strengthLabels = {
  length: 'Au moins 8 caractères',
  uppercase: 'Une majuscule',
  lowercase: 'Une minuscule',
  digit: 'Un chiffre',
  special: 'Un caractère spécial (!@#$%^&*)',
};

const step1Valid = computed(() => {
  const mail = email.value?.trim() || '';
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail);
  return Boolean(last_name.value?.trim() && first_name.value?.trim() && emailOk);
});
const step2Valid = computed(() =>
  Boolean(tel.value?.trim() && adresse.value?.trim() && fonction.value?.trim())
);

function goRegisterStep(step) {
  registerStep.value = step;
  if (step === 3 && captchaProvider.value === 'turnstile') {
    nextTick(async () => {
      turnstileWidgetId.value = null;
      turnstileToken.value = '';
      await initTurnstileWidget();
    });
  }
}

function onFormSubmit() {
  if (registerStep.value !== 3) {
    if (registerStep.value === 1 && step1Valid.value) goRegisterStep(2);
    else if (registerStep.value === 2 && step2Valid.value) goRegisterStep(3);
    return;
  }
  handleSubmit();
}

const isFormValid = computed(() => {
  const base =
    last_name.value &&
    first_name.value &&
    username.value &&
    email.value &&
    tel.value &&
    adresse.value &&
    fonction.value &&
    password.value &&
    password.value === passwordConfirm.value &&
    Object.values(passwordChecks.value).every(Boolean);
  if (captchaProvider.value === 'turnstile') {
    return base && !!turnstileToken.value;
  }
  if (captchaProvider.value === 'none') {
    return base;
  }
  return false;
});

function generateLogin(nom, prenom, rankNumber = null) {
  if (!nom || !prenom) return '';
  if (rankNumber == null) return ''; // ne pas afficher de login avec "1" avant que le rang API soit chargé
  const cleanNom = (nom.trim() || '').toLowerCase();
  const prenoms = (prenom.trim() || '').split(/\s+/).filter(Boolean);
  const dernierPrenom = (prenoms.length ? prenoms[prenoms.length - 1] : '').toLowerCase();
  const deuxPremieresNom = cleanNom.substring(0, 2).toUpperCase();
  const deuxDernieresPrenom =
    dernierPrenom.length >= 2
      ? dernierPrenom.substring(dernierPrenom.length - 2).toUpperCase()
      : dernierPrenom.toUpperCase();
  const rang = Math.max(rankNumber, 1);
  const suffixeNumerique = 200000 + 10000 + rang;
  return deuxPremieresNom + deuxDernieresPrenom + suffixeNumerique;
}

function updateGeneratedLogin() {
  username.value = generateLogin(last_name.value, first_name.value, nextClientRank.value);
}

function validatePassword() {
  const p = password.value;
  passwordChecks.value = {
    length: p.length >= 8,
    uppercase: /[A-Z]/.test(p),
    lowercase: /[a-z]/.test(p),
    digit: /[0-9]/.test(p),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p),
  };
}

function getDetailedErrorMessage(error) {
  const validationMessages = formatValidationErrors(error?.response?.data);
  return getErrorMessage(error, { validationMessages });
}

function resetTurnstileWidget() {
  resetTurnstileWidgetUtil(turnstileWidgetId.value, () => {
    turnstileToken.value = '';
  });
}

async function initTurnstileWidget() {
  if (!turnstileSiteKey.value) {
    turnstileSiteKey.value = resolveTurnstileSiteKey();
  }
  if (captchaProvider.value !== 'turnstile') {
    if (process.env.NODE_ENV !== 'production') {
      errorMessage.value = '';
    } else {
      errorMessage.value =
        'La vérification de sécurité est indisponible pour le moment. Réessayez dans quelques instants.';
    }
    return;
  }
  try {
    await nextTick();
    const el = turnstileContainerRef.value;
    if (!el || turnstileWidgetId.value != null) return;
    turnstileWidgetId.value = await mountTurnstileWidget(el, turnstileSiteKey.value, {
      onSuccess: (token) => { turnstileToken.value = token; },
      onError: () => { turnstileToken.value = ''; },
      onExpired: () => { turnstileToken.value = ''; },
    });
  } catch (_) {
    errorMessage.value = 'La vérification de sécurité est indisponible. Vérifie ton blocage de publicités et réessaie.';
  }
}

function closeEmailVerificationModal() {
  showEmailVerificationModal.value = false;
}

function closeSuccessModal() {
  showSuccessModal.value = false;
}

async function openEmailVerificationModal() {
  verificationCode.value = '';
  verificationError.value = '';
  showSuccessModal.value = false;
  await nextTick();
  showEmailVerificationModal.value = true;
}

async function continueToLogin() {
  showSuccessModal.value = false;
  await router.push({
    path: '/connexion',
    query: { verified: '1', redirect: '/analyse-patrimoniale/mon-analyse' },
  });
}

async function handleSubmit() {
  if (password.value !== passwordConfirm.value) {
    errorMessage.value = 'Les mots de passe ne correspondent pas.';
    return;
  }
  if (!username.value) {
    errorMessage.value = "Veuillez remplir le nom et le prénom pour générer l'identifiant.";
    return;
  }
  if (captchaProvider.value === 'turnstile' && !turnstileToken.value) {
    errorMessage.value = 'Valide la vérification de sécurité avant de continuer.';
    return;
  }
  if (captchaProvider.value !== 'turnstile' && captchaProvider.value !== 'none') {
    errorMessage.value = 'La vérification de sécurité est indisponible. Réessayez dans quelques instants.';
    return;
  }

  const guarded = await registerSubmitGuard.run(async () => {
    await executeRegisterSubmit();
  });
  if (!guarded.ok && (guarded.reason === 'in_flight' || guarded.reason === 'cooldown')) {
    errorMessage.value = 'Inscription en cours, veuillez patienter avant de réessayer.';
  }
}

async function executeRegisterSubmit() {
  loading.value = true;
  errorMessage.value = '';

  try {
    const payload = {
      last_name: last_name.value,
      first_name: first_name.value,
      username: username.value,
      email: email.value,
      tel: tel.value,
      password: password.value,
      role: 'client',
      type_client: 'standard',
      adresse: adresse.value,
      fonction: fonction.value,
    };
    if (turnstileToken.value) {
      payload.turnstile_token = turnstileToken.value;
    }

    await axios.post(
      `${API_PREFIX}/utilisateurs/create/`,
      payload,
      { timeout: 90000, withCredentials: true }
    );
    await openEmailVerificationModal();
  } catch (error) {
    const throttleMsg = formatThrottleMessage(error);
    if (throttleMsg) {
      errorMessage.value = throttleMsg;
      turnstileToken.value = '';
      return;
    }
    const data = error.response?.data;
    const msg = data?.detail || data?.error;
    const isCaptchaError =
      typeof msg === 'string' &&
      (msg.includes('reCAPTCHA') ||
        msg.includes('recaptcha') ||
        msg.includes('Turnstile') ||
        msg.includes('captcha'));
    if (captchaProvider.value === 'turnstile') {
      resetTurnstileWidget();
    }
    if (isCaptchaError) {
      errorMessage.value = 'La vérification de sécurité a été refusée. Réessayez.';
    } else if (error?.code === 'ECONNABORTED') {
      errorMessage.value = 'Le serveur met trop de temps à répondre. Vérifie si votre compte a bien été créé puis réessayez.';
    } else if (error?.code === 'ERR_NETWORK') {
      errorMessage.value = 'Un problème de connexion temporaire a empêché la création du compte. Rechargez la page puis réessayez.';
    } else if (error?.response?.status === 503) {
      errorMessage.value = 'Le service est temporairement indisponible. Réessayez dans quelques instants.';
    } else {
      errorMessage.value = getDetailedErrorMessage(error).message;
    }
  } finally {
    loading.value = false;
  }
}

const verificationCode = ref('');
const verificationError = ref('');
const verificationLoading = ref(false);
const resendLoading = ref(false);

async function submitEmailVerification() {
  if (!verificationCode.value.trim()) {
    verificationError.value = 'Veuillez saisir le code de vérification.';
    return;
  }
  verificationLoading.value = true;
  verificationError.value = '';
  try {
    await axios.post(
      `${API_PREFIX}/email-verification/verify/`,
      {
        email: email.value,
        code: verificationCode.value.trim(),
      },
      { timeout: 30000 }
    );
    showEmailVerificationModal.value = false;
    showSuccessModal.value = true;
    trackSignUp('email');
  } catch (error) {
    verificationError.value = getDetailedErrorMessage(error).message;
  } finally {
    verificationLoading.value = false;
  }
}

async function resendVerificationCode() {
  const guarded = await resendSubmitGuard.run(async () => {
    resendLoading.value = true;
    verificationError.value = '';
    try {
      await axios.post(
        `${API_PREFIX}/email-verification/resend/`,
        { email: email.value, username: username.value },
        { timeout: 30000 }
      );
    } catch (error) {
      verificationError.value =
        formatThrottleMessage(error) || getDetailedErrorMessage(error).message;
    } finally {
      resendLoading.value = false;
    }
  });
  if (!guarded.ok) {
    verificationError.value = 'Un code a déjà été demandé récemment. Patientez avant de renvoyer.';
  }
}

onMounted(async () => {
  try {
    const prefill = loadRegisterPrefillFromDiagnostic(route.query);
    if (prefill) {
      if (prefill.last_name) last_name.value = prefill.last_name;
      if (prefill.first_name) first_name.value = prefill.first_name;
      if (prefill.email) email.value = prefill.email;
      if (prefill.tel) tel.value = prefill.tel;
      if (prefill.adresse) adresse.value = prefill.adresse;
      if (prefill.fonction) fonction.value = prefill.fonction;
    }
  } catch (_) {
    /* ignore */
  }

  try {
    const res = await axios.get(`${API_PREFIX}/next-client-rank/`, { timeout: 60000 });
    if (res.data && typeof res.data.next_client_rank === 'number') {
      nextClientRank.value = res.data.next_client_rank;
    } else {
      nextClientRank.value = 1;
    }
    updateGeneratedLogin();
  } catch (_) {
    nextClientRank.value = 1;
    updateGeneratedLogin();
  }

  turnstileSiteKey.value = resolveTurnstileSiteKey();
  // Turnstile monté à l’étape 3 (Sécurité) uniquement
});

watch(turnstileSiteKey, async (key) => {
  if (key && registerStep.value === 3 && turnstileWidgetId.value == null) {
    await nextTick();
    await initTurnstileWidget();
  }
});

watch([last_name, first_name], () => updateGeneratedLogin());
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  font-family: var(--font-primary);
}
.auth-page__wrap {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.auth-card {
  background: #fff;
  border-radius: 1.25rem;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.08), 0 0 0 1px rgba(15, 23, 42, 0.04);
  padding: 2rem 1.75rem;
  width: 100%;
}
.card-enter-active {
  transition: opacity 0.4s ease, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.card-enter-from {
  opacity: 0;
  transform: translateY(16px);
}
.auth-card__header {
  text-align: center;
  padding-bottom: 1.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(212, 160, 23, 0.2);
}
.auth-card__header--register {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 2.25rem 1.5rem 2.75rem;
  background:
    linear-gradient(180deg, rgba(84, 57, 41, 0.08), transparent 40%),
    var(--brand-brown);
  border-bottom: none;
  border-radius: 0;
  box-sizing: border-box;
  text-align: center;
}
.auth-card__title {
  font-family: var(--font-display);
  font-size: clamp(1.75rem, 1.35rem + 1.5vw, 2.5rem);
  font-weight: 600;
  color: #f7f1e8;
  margin: 0 0 0.5rem 0;
  text-align: center;
  letter-spacing: -0.02em;
  line-height: 1.15;
}
.auth-card__title--register {
  color: #f7f1e8 !important;
  -webkit-text-fill-color: #f7f1e8;
}
.auth-card__subtitle {
  font-size: var(--text-prose);
  color: #64748b;
  margin: 0;
  text-align: center;
  line-height: var(--line-normal);
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;
}
.auth-card__subtitle--register {
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.9rem;
  max-width: 440px;
  line-height: 1.55;
}
.auth-card__subtitle-mark {
  color: #ef4444;
  font-weight: 700;
  margin-right: 0.2rem;
}

.progress-brand__labels {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.55rem;
  font-family: var(--font-body);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  text-align: center;
}

.progress-brand__labels .is-active {
  color: var(--brand-brown);
}

.progress-brand {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.progress-brand__step {
  height: 3px;
  border-radius: 999px;
  background: #e2e8f0;
}

.progress-brand__step.is-active,
.progress-brand__step.is-done {
  background: var(--brand-gold);
}

.auth-form__group {
  margin-bottom: 1.25rem;
}
.auth-form__label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  margin-bottom: 0.5rem;
}
.auth-form__input-wrap {
  position: relative;
}
.auth-form__icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  color: #94a3b8;
}
.auth-form__input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: #0f172a;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.auth-form__input:focus {
  outline: none;
  border-color: #d89c24;
  box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.15);
}
.auth-form__input--with-btn {
  padding-right: 3rem;
}
.auth-form__toggle-pwd {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  padding: 0.25rem;
  color: #64748b;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 0.5rem;
}
.auth-form__toggle-pwd:hover {
  color: #0f172a;
  background: #f1f5f9;
}
.auth-form__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.75rem;
}
.auth-form__submit {
  width: 100%;
  padding: 0 var(--space-5);
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brand-brown);
  background: var(--brand-gold);
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease), box-shadow var(--dur-fast) var(--ease);
  min-height: 48px;
}
.auth-form__submit:hover:not(:disabled) {
  background: var(--brand-gold-light);
  color: var(--brand-ink);
  transform: translateY(-1px);
  box-shadow: var(--shadow-md);
}
.auth-form__submit:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.auth-form__spinner {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid color-mix(in srgb, var(--brand-ink) 25%, transparent);
  border-top-color: var(--brand-ink);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.auth-form__footer {
  margin: 1rem 0 0;
  font-size: 0.875rem;
  color: #64748b;
  text-align: center;
}
.auth-form__link {
  font-weight: 500;
  color: var(--brand-gold-dark);
  text-decoration: none;
}
.auth-form__link:hover {
  color: var(--brand-navy);
}

.auth-page--register .auth-page__wrap--wide {
  max-width: 640px;
  width: 100%;
  flex: 1;
  justify-content: flex-start;
  padding: 0 1rem 2rem;
  margin: -1.5rem auto 0;
}
.auth-card--wide {
  padding: 2rem 1.75rem;
  overflow: hidden;
  border-radius: 16px;
  border-top: 3px solid var(--brand-gold);
  box-shadow: var(--shadow-lg);
}
.auth-form--grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.25rem;
}
.auth-form__group--full {
  grid-column: 1 / -1;
}
.auth-form__input--readonly {
  background: #f1f5f9;
  cursor: not-allowed;
  color: #64748b;
}
.auth-form__strength {
  margin-top: 0.5rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-radius: 0.75rem;
  border-left: 3px solid #e2e8f0;
}
.auth-form__strength-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: #64748b;
  margin-bottom: 0.25rem;
}
.auth-form__strength-item:last-child {
  margin-bottom: 0;
}
.auth-form__strength-item--ok {
  color: #059669;
}
.auth-form__strength-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: #cbd5e1;
}
.auth-form__field-error {
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  color: #b91c1c;
}
.auth-form__submit-wrap {
  margin-top: 0.25rem;
  display: flex;
  gap: var(--space-3);
  flex-wrap: wrap;
  align-items: stretch;
}
.auth-form__nav .btn-brand,
.auth-form__nav .auth-form__submit {
  flex: 1 1 140px;
}

.auth-form__continue,
.auth-form__nav .btn-brand--primary {
  background: var(--brand-gold) !important;
  border-color: var(--brand-gold) !important;
  color: var(--brand-brown) !important;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.auth-form__continue:hover:not(:disabled),
.auth-form__nav .btn-brand--primary:hover:not(:disabled) {
  background: var(--brand-gold-light, #e0b45a) !important;
  border-color: var(--brand-gold-light, #e0b45a) !important;
  color: var(--brand-brown) !important;
}
.auth-form__recaptcha {
  font-size: 0.75rem;
  color: #94a3b8;
  margin: 0;
}
.auth-form__recaptcha a {
  color: var(--brand-gold-dark);
  text-decoration: none;
}
.turnstile-widget {
  display: flex;
  justify-content: center;
  min-height: 70px;
}
.auth-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}
.auth-modal-card {
  background: #fff;
  border-radius: 1.25rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  padding: 2rem;
  max-width: 400px;
  width: 100%;
  text-align: center;
}
.auth-modal-icon {
  margin-bottom: 1rem;
}
.auth-modal-brand {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
}
.auth-modal-title {
  font-family: var(--font-display);
  font-size: var(--text-xl);
  font-weight: var(--font-weight-bold);
  color: #0f172a;
  margin: 0 0 0.5rem 0;
}
.auth-modal-text {
  font-size: var(--text-prose);
  color: #64748b;
  margin: 0 0 1.5rem 0;
}
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}
.modal-enter-active .auth-modal-card,
.modal-leave-active .auth-modal-card {
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .auth-modal-card,
.modal-leave-to .auth-modal-card {
  transform: scale(0.95);
}
@media (max-width: 640px) {
  .auth-form--grid {
    grid-template-columns: 1fr;
  }
}
</style>
